import { Component, Inject, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CreateColumnForm } from './create-column.interface';
import { Subject, takeUntil } from 'rxjs';
import { CrmService } from '../../services/crm.service';
import { NotificationService } from '../../services/notifications.service';
import { IColumn } from '../../interfaces';
import { trimValidator } from '../../helpers';
import * as _ from 'lodash';
import { activeTranslations } from '../../enum/constants';

@Component({
  selector: 'app-create-column',
  templateUrl: './create-column.component.html',
  styleUrls: ['./create-column.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateColumnComponent implements OnInit, OnDestroy {
  // Destroy
  private readonly destroy$: Subject<null> = new Subject<null>();

  // Var-s
  private readonly funnel_id!: string;
  private readonly position!: number;

  public readonly column!: IColumn;
  public createColumnForm!: FormGroup<CreateColumnForm>;

  public isSubmitted: boolean = false;
  public hasChange: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<CreateColumnComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public crmService: CrmService,
    public notificationService: NotificationService,
  ) {
    this.funnel_id = this.data.data.funnel_id;
    this.column = this.data.data.column;
    this.position = this.data.data.position;
  }

  ngOnInit() {
    this.initForm();
  }

  ngOnDestroy() {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private initForm() {
    if (this.column) {
      this.createColumnForm = new FormGroup({
        name: new FormControl(this.column.name, [Validators.required, trimValidator]),
        bg_color: new FormControl(this.column.bg_color, Validators.required),
        text_color: new FormControl(this.column.text_color, Validators.required),
      });

      this.onCreateGroupFormValueChange();
    } else {
      this.createColumnForm = new FormGroup({
        name: new FormControl('', [Validators.required, trimValidator]),
        bg_color: new FormControl('#000000', Validators.required),
        text_color: new FormControl('#ffffff', Validators.required),
      });
    }
  }

  private onCreateGroupFormValueChange() {
    const initialValue: any = { ...this.createColumnForm.getRawValue() };
    this.createColumnForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: any) => {
      this.hasChange = !_.isEqual(value, initialValue);
    });
  }

  public onSaveClick() {
    this.isSubmitted = true;
    const { name, bg_color, text_color } = this.createColumnForm.getRawValue();

    const payload = {
      funnel_id: this.funnel_id,
      name: name,
      bg_color: bg_color,
      text_color: text_color,
      position: this.position,
    };

    if (!this.createColumnForm.valid) return;

    if (this.column) {
      this.crmService
        .updateColumn(payload, this.column.id)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_saved,
            );
            this.dialogRef.close({
              success: true,
            });
          },
          error: () => {
            this.notificationService.showError(
              '',
              activeTranslations.notifications_texts.error.error_saving,
            );
          },
        });
    } else {
      this.crmService
        .createColumn(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_saved,
            );
            this.dialogRef.close({
              success: true,
            });
          },
          error: () => {
            this.notificationService.showError(
              '',
              activeTranslations.notifications_texts.error.error_saving,
            );
          },
        });
    }
  }

  public onCancelClick() {
    this.dialogRef.close();
  }
}
